import { Button, Spin } from "antd";
import React, { useEffect, useState } from "react";
import Sections from "./Sections";
import AccountManagement from "./components/AccountManagement";
import { EditProfile, EyeInvisible, EyeVisible } from "../../assets/icons";
import { httpService } from "../../base/services/httpService.service";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useNotification } from "../../base/hooks/useNotification";
import AuthService from "../../base/services/authentication.service";
import { UserRolesEnum } from "../auth/userRoles.enum";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useTranslation } from "../../i18n/LanguageContext";

const listStyle = {
   color: "#3A3737",
   fontSize: "16px",
   fontFamily: "Lato"
};

const LoginInfo = () => {
   const { t } = useTranslation();
   const { api: notificationApi, notificationContextHolder } = useNotification();
   const [isLoadingEmail, setIsLoadingEmail] = useState(false);
   const [isLoadingPassword, setIsLoadingPassword] = useState(false);
   const [isEditPassword, setIsEditPassword] = useState(false);
   const [isEditEmail, setIsEditEmail] = useState(false);
   const isSuperAdmin = AuthService.isAuthorized([UserRolesEnum.SuperAdmin]);
   const { executeRecaptcha } = useGoogleReCaptcha();

   const emailValidationSchema = Yup.object().shape({
      email: Yup.string()
         .email(t("invalid-email-address-validation"))
         .required(t("email-required-validation"))
   });
   const passwordValidationSchema = Yup.object().shape({
      oldPassword: Yup.string().required(t("current-password-required-validation")),
      newPassword: Yup.string()
         .min(8, t("password-min-length-validation"))
         .matches(
            /^(?=.*[A-Z])(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*\d).*$/,
            t("password-complexity-validation")
         )
         .required(t("new-password-required-validation")),
      confirmNewPassword: Yup.string()
         .oneOf([Yup.ref("newPassword"), null], t("passwords-must-match-validation"))
         .required(t("confirm-new-password-required-validation"))
   });

   const handleEditClick = () => {
      setIsEditPassword(!isEditPassword);
   };
   const handleEditEmail = () => {
      setIsEditEmail(!isEditEmail);
   };

   const handleCancelClick = (resetForm) => {
      resetForm();
      setIsEditPassword(false);
   };
   const handleCancelEmailChange = (resetForm) => {
      resetForm();
      setIsEditEmail(false);
   };

   const [formInitialValues, setFormInitialValues] = useState({
      email: "",
      oldPassword: "",
      newPassword: "",
      confirmNewPassword: ""
   });

   const getEmail = () => {
      httpService.get(
         `Accounts/email`,
         (res) => {
            const data = res?.data;

            setFormInitialValues({
               email: data?.email
            });
         },
         () => {}
      );
   };

   useEffect(() => {
      getEmail();
   }, []);

   const editEmail = async (values, resetForm) => {
      if(!executeRecaptcha) {
         return;
      }
      setIsLoadingEmail(true);

      const recaptchaToken = await executeRecaptcha('login');
      try {
         const updatedValues = {
            ...values
         };

         await httpService.put(
            `Accounts/email`,
            updatedValues,
            (res) => {
               setFormInitialValues(res?.data);
               setIsEditEmail(false);
               resetForm();
               notificationApi.success("user", {
                  message: t("email-changed-success-title"),
                  description: t("email-changed-success-description")
               });
            },
            () => {},
            {
               headers: {
                  'g-recaptcha-response': recaptchaToken
               }
            }
         );
         getEmail();
      } catch (error) {
         notificationApi.error("user", {
            message: t("generic-problem-occurred-title"),
            description: t("email-update-error-description")
         });
      } finally {
         setIsLoadingEmail(false);
      }
   };

   const [error, setError] = useState("");

   const editPassword = async (values, resetForm) => {
      const updatedValues = {
         oldPassword: values?.oldPassword,
         newPassword: values?.newPassword,
         confirmNewPassword: values?.confirmNewPassword
      };

      try {
         setIsLoadingPassword(true);
         await httpService.post(
            `Accounts/password/change`,
            updatedValues,
            () => {
               setIsEditPassword(false);
               resetForm();
               notificationApi.success("request-lock", {
                  message: t("password-changed-success-title"),
                  description: t("password-changed-success-description")
               });
               setError("");
            },
            () => {}
         );
      } catch (error) {
         if (
            error.response &&
            error.response.data &&
            error.response.data.errors &&
            error.response.data.errors[0].code === "validation"
         ) {
            const errorMessage = error.response.data.errors[0].message;
            setError(errorMessage);
         } else {
            notificationApi.error("request-lock", {
               message: t("generic-problem-occurred-title"),
               description: t("password-update-error-description")
            });
         }
      } finally {
         setIsLoadingPassword(false);
      }
   };

   const [passwordVisibility, setPasswordVisibility] = useState({
      oldPassword: "password",
      newPassword: "password",
      confirmNewPassword: "password"
   });

   const [icons, setIcons] = useState({
      oldPassword: EyeInvisible,
      newPassword: EyeInvisible,
      confirmNewPassword: EyeInvisible
   });

   const handleToggle = (field) => {
      setPasswordVisibility((prevState) => ({
         ...prevState,
         [field]: prevState[field] === "password" ? "text" : "password"
      }));

      setIcons((prevIcons) => ({
         ...prevIcons,
         [field]: prevIcons[field] === EyeInvisible ? EyeVisible : EyeInvisible
      }));
   };

   const isStaff = AuthService.isAuthorized([UserRolesEnum.Staff]);
   const isManager = AuthService.isAuthorized([UserRolesEnum.Manager]);

   return (
      <>
         {notificationContextHolder}
         <div style={{ padding: "40px 32px", backgroundColor: "#F5F9FC", minHeight: "100%" }}>
            <div
               className="d-flex justify-content-between align-items-center flex-wrap"
               style={{
                  backgroundColor: "#fff",
                  padding: "24px 32px",
                  borderRadius: "8px 8px 0 0"
               }}
            >
               <span
                  style={{
                     color: "#003DAA",
                     fontWeight: 600,
                     fontFamily: "poppins",
                     fontSize: "24px"
                  }}
               >
                  {t("login-information-label")}
               </span>
            </div>
            <div
               style={{
                  border: "1.5px solid #DFE6FF",
                  borderRadius: "0 0 8px 9px",
                  padding: "24px 32px",
                  backgroundColor: "#fff"
               }}
            >
               <Formik
                        initialValues={formInitialValues}
                        validationSchema={
                           isEditEmail ? emailValidationSchema : passwordValidationSchema
                        }
                        onSubmit={(values, { resetForm }) => {
                           if (isEditEmail) {
                              editEmail(values, resetForm);
                           } else {
                              editPassword(values, resetForm);
                           }
                        }}
                        enableReinitialize
                     >
                        {({
                           handleSubmit,
                           handleChange,
                           touched,
                           values,
                           errors,
                           dirty,
                           setFieldValue,
                           isSubmitting,
                           resetForm
                        }) => (
                           <Form>
                              {isEditPassword ? (
                                 <>
                                    <div className="d-md-flex" style={{ gap: "40px" }}>
                                       <div
                                          className="d-flex flex-column w-100 w-md-50"
                                          style={{ gap: "8px" }}
                                       >
                                          <div
                                             className="d-flex flex-column"
                                             style={{ gap: "40px" }}
                                          >
                                             <div>
                                                <Sections
                                                   fieldType="oldPassword"
                                                   sectionName={t("account-information-label")}
                                                   propertyName={t("current-password-label")}
                                                   propertyValue={values?.oldPassword}
                                                   name="oldPassword"
                                                   isEditPassword={isEditPassword}
                                                   handleChange={handleChange}
                                                   placeholder={t("enter-current-password-placeholder")}
                                                   isRequired={true}
                                                   handleToggle={() => handleToggle("oldPassword")}
                                                   type={passwordVisibility.oldPassword}
                                                   icon={icons.oldPassword}
                                                />

                                                {errors.oldPassword ? (
                                                   <div className="text-danger">
                                                      {errors.oldPassword}
                                                   </div>
                                                ) : (
                                                   error && (
                                                      <div className="text-danger">{error}</div>
                                                   )
                                                )}
                                             </div>
                                             <div>
                                                <Sections
                                                   fieldType="newPassword"
                                                   propertyName={t("new-password-label")}
                                                   propertyValue={values?.newPassword}
                                                   name="newPassword"
                                                   isEditPassword={isEditPassword}
                                                   handleChange={handleChange}
                                                   placeholder={t("enter-new-password-placeholder")}
                                                   isRequired={true}
                                                   handleToggle={() => handleToggle("newPassword")}
                                                   type={passwordVisibility.newPassword}
                                                   icon={icons.newPassword}
                                                />
                                                {/* {<div className="text-danger">{error}</div>} */}
                                                {errors.newPassword && (
                                                   <div className="text-danger">
                                                      {errors.newPassword}
                                                   </div>
                                                )}
                                             </div>
                                             <div>
                                                <Sections
                                                   propertyName={t("confirm-password-label")}
                                                   propertyValue={values?.confirmNewPassword}
                                                   name="confirmNewPassword"
                                                   isEditPassword={isEditPassword}
                                                   handleChange={handleChange}
                                                   placeholder={t("enter-new-password-again-placeholder")}
                                                   isRequired={true}
                                                   handleToggle={() =>
                                                      handleToggle("confirmNewPassword")
                                                   }
                                                   type={passwordVisibility.confirmNewPassword}
                                                   icon={icons.confirmNewPassword}
                                                />
                                                {errors.confirmNewPassword && (
                                                   <div className="text-danger">
                                                      {errors.confirmNewPassword}
                                                   </div>
                                                )}
                                             </div>
                                          </div>
                                          <div className="footer-buttons flex-wrap">
                                             <Button
                                                className="edit-outlined"
                                                onClick={() => handleCancelClick(resetForm)}
                                                disabled={isLoadingPassword}
                                             >
                                                {t("cancel")}
                                             </Button>
                                             <Button
                                                className="edit-button-workspace"
                                                htmlType="submit"
                                                onClick={handleSubmit}
                                                disabled={isLoadingPassword}
                                             >
                                                {isLoadingPassword ? <Spin /> : t("save-changes-button")}
                                             </Button>
                                          </div>
                                       </div>
                                       <div
                                          className="w-100 w-md-50"
                                          style={{
                                             border: "1px solid #DFE6FF",
                                             padding: "32px",
                                             borderRadius: "8px",
                                             backgroundColor: "#FBFDFF",
                                             height: "fit-content"
                                          }}
                                       >
                                          <label
                                             style={{
                                                color: "#3A3737",
                                                fontSize: "16px",
                                                fontFamily: "Lato"
                                             }}
                                          >
                                             {t("password-hint-intro-text")}
                                          </label>
                                          <ul className="d-flex flex-column">
                                             <li style={listStyle}>{t("password-hint-length")}</li>
                                             <li style={listStyle}>{t("password-hint-uppercase")}</li>
                                             <li style={listStyle}>{t("password-hint-lowercase")}</li>
                                             <li style={listStyle}>{t("password-hint-number")}</li>
                                             <li style={listStyle}>{t("password-hint-special")}</li>
                                          </ul>
                                       </div>
                                    </div>
                                 </>
                              ) : isEditEmail ? (
                                 <div
                                    className="d-flex flex-column w-100 w-md-50"
                                    style={{ gap: "8px" }}
                                 >
                                    <div className="w-md-50">
                                       <Sections
                                          sectionName={t("account-information-label")}
                                          propertyName={t("email-label")}
                                          name="email"
                                          propertyValue={values?.email}
                                          isEditEmail={isEditEmail}
                                          handleChange={handleChange}
                                          isRequired={true}
                                       />
                                       {errors.email && (
                                          <div className="text-danger">{errors.email}</div>
                                       )}
                                    </div>

                                    <div className="footer-buttons flex-wrap w-md-50">
                                       <Button
                                          className="edit-outlined"
                                          onClick={() => handleCancelEmailChange(resetForm)}
                                          disabled={isLoadingEmail}
                                       >
                                          {t("cancel")}
                                       </Button>
                                       <Button
                                          className="edit-button-workspace"
                                          htmlType="submit"
                                          disabled={!dirty || isLoadingEmail}
                                          loading={isLoadingEmail}
                                       >
                                          {t("save-changes-button")}
                                       </Button>
                                    </div>
                                 </div>
                              ) : (
                                 <div
                                    className="d-md-flex "
                                    style={{
                                       gap: "48px"
                                    }}
                                 >
                                    <div className="d-flex justify-content-between align-items-center w-100">
                                       <Sections
                                          sectionName={t("account-information-label")}
                                          propertyName={t("email-label")}
                                          propertyValue={values?.email}
                                          isEditEmail={isEditEmail} //test here
                                       />
                                       {!isStaff && !isManager && (
                                          <Button
                                             style={{ height: "32px" }}
                                             icon={<img src={EditProfile} alt="" />}
                                             className="edit-outlined"
                                             onClick={handleEditEmail}
                                          ></Button>
                                       )}
                                    </div>
                                    <div
                                       className="d-flex justify-content-between align-items-center w-100"
                                       style={{ gap: "8px" }}
                                    >
                                       <Sections
                                          propertyName={t("password-label")}
                                          propertyValue={t("change-password-here-text")}
                                          isEditPassword={isEditPassword}
                                       />
                                       <Button
                                          style={{ height: "32px" }}
                                          icon={<img src={EditProfile} alt="" />}
                                          className="edit-outlined"
                                          onClick={handleEditClick}
                                       ></Button>
                                    </div>
                                 </div>
                              )}
                           </Form>
                        )}
               </Formik>
            </div>
            {!isSuperAdmin && (
               <div style={{ marginTop: "40px" }}>
                  <AccountManagement />
               </div>
            )}
         </div>
      </>
   );
};
export default LoginInfo;
